import React, { useCallback, useEffect, useRef, useState } from "react";
import {
    Box,
    Button,
    Chip,
    TextField,
    ToggleButton,
    InputAdornment,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Alert,
    CircularProgress,
    Typography,
} from "@mui/material";
import {
    Search as SearchIcon,
    Archive as ArchiveIcon,
    Unarchive as UnarchiveIcon,
} from "@mui/icons-material";
import WorkOrderService from "../../../services/WorkOrder.service";
import ProjectPicker from "../../pickers/ProjectPicker";
import WorkOrdersList from "./WorkOrdersList";
import WorkOrderViewDialog from "./WorkOrderViewDialog";

const STATUS_FILTERS = [
    { value: "", label: "All" },
    { value: "draft", label: "Draft" },
    { value: "released", label: "Released" },
    { value: "in_progress", label: "In Progress" },
    { value: "complete", label: "Complete" },
];

function ArchiveDialog({ open, workOrder, onClose, onArchived }) {
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");

    useEffect(() => {
        if (open) setError("");
    }, [open]);

    const handleArchive = async () => {
        setLoading(true);
        setError("");
        try {
            await WorkOrderService.archive(workOrder.id);
            await onArchived();
            onClose();
        } catch (err) {
            setError(err.response?.data?.detail || "Failed to archive work order.");
        } finally {
            setLoading(false);
        }
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
            <DialogTitle>Archive Work Order</DialogTitle>
            <DialogContent>
                {error && (
                    <Alert severity="error" sx={{ mb: 1 }}>
                        {error}
                    </Alert>
                )}
                <Typography variant="body2">
                    Archive <strong>{workOrder?.wo_number}</strong>? It will be hidden from the
                    default list but can be restored later.
                </Typography>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} disabled={loading}>
                    Cancel
                </Button>
                <Button
                    color="warning"
                    variant="contained"
                    onClick={handleArchive}
                    disabled={loading}
                    startIcon={loading ? <CircularProgress size={18} /> : <ArchiveIcon />}
                >
                    Archive
                </Button>
            </DialogActions>
        </Dialog>
    );
}

function RestoreDialog({ open, workOrder, onClose, onRestored }) {
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");

    useEffect(() => {
        if (open) setError("");
    }, [open]);

    const handleRestore = async () => {
        setLoading(true);
        setError("");
        try {
            await WorkOrderService.unarchive(workOrder.id);
            await onRestored();
            onClose();
        } catch (err) {
            setError(err.response?.data?.detail || "Failed to restore work order.");
        } finally {
            setLoading(false);
        }
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
            <DialogTitle>Restore Work Order</DialogTitle>
            <DialogContent>
                {error && (
                    <Alert severity="error" sx={{ mb: 1 }}>
                        {error}
                    </Alert>
                )}
                <Typography variant="body2">
                    Restore <strong>{workOrder?.wo_number}</strong> to the active list?
                </Typography>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} disabled={loading}>
                    Cancel
                </Button>
                <Button
                    color="primary"
                    variant="contained"
                    onClick={handleRestore}
                    disabled={loading}
                    startIcon={loading ? <CircularProgress size={18} /> : <UnarchiveIcon />}
                >
                    Restore
                </Button>
            </DialogActions>
        </Dialog>
    );
}

export default function WorkOrders() {
    const [statusFilter, setStatusFilter] = useState("");
    const [search, setSearch] = useState("");
    const [projectFilter, setProjectFilter] = useState(null);
    const [dateFrom, setDateFrom] = useState("");
    const [dateTo, setDateTo] = useState("");
    const [showArchived, setShowArchived] = useState(false);
    const [workOrders, setWorkOrders] = useState([]);
    const [page, setPage] = useState(1);
    const [hasMore, setHasMore] = useState(false);
    const [loading, setLoading] = useState(false);
    const [viewDialog, setViewDialog] = useState({ open: false, item: null });
    const [archiveTarget, setArchiveTarget] = useState(null);
    const [restoreTarget, setRestoreTarget] = useState(null);
    const debounceRef = useRef(null);

    const hasArchiveFilter = Boolean(search.trim() || projectFilter || dateFrom || dateTo);

    const runSearch = useCallback((targetPage, append) => {
        // Archived work orders accumulate for years — require a filter before
        // fetching so we never dump the entire archive on load.
        if (showArchived && !hasArchiveFilter) {
            setWorkOrders([]);
            setHasMore(false);
            setLoading(false);
            return;
        }
        setLoading(true);
        const params = { page: targetPage, ordering: "-created" };
        if (statusFilter) params.status = statusFilter;
        if (search.trim()) params.search = search.trim();
        if (projectFilter) params.project = projectFilter.id;
        if (dateFrom) params.date_from = dateFrom;
        if (dateTo) params.date_to = dateTo;
        if (showArchived) params.archived = "1";
        WorkOrderService.listWorkOrders(params)
            .then((res) => {
                const results = res.data.results || [];
                setWorkOrders((prev) => (append ? [...prev, ...results] : results));
                setHasMore(Boolean(res.data.next));
                setPage(targetPage);
            })
            .catch(() => {
                if (!append) setWorkOrders([]);
                setHasMore(false);
            })
            .finally(() => setLoading(false));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [statusFilter, search, projectFilter, dateFrom, dateTo, showArchived, hasArchiveFilter]);

    useEffect(() => {
        runSearch(1, false);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [statusFilter, projectFilter, dateFrom, dateTo, showArchived]);

    const handleSearchChange = (e) => {
        const val = e.target.value;
        setSearch(val);
        clearTimeout(debounceRef.current);
        debounceRef.current = setTimeout(() => runSearch(1, false), 350);
    };

    const handleLoadMore = () => runSearch(page + 1, true);

    const handleChanged = () => runSearch(page, false);

    const activeFilterCount = [search.trim(), statusFilter, projectFilter, dateFrom, dateTo, showArchived]
        .filter(Boolean).length;

    const clearFilters = () => {
        clearTimeout(debounceRef.current);
        setSearch("");
        setStatusFilter("");
        setProjectFilter(null);
        setDateFrom("");
        setDateTo("");
        setShowArchived(false);
    };

    return (
        <Box>
            <Box sx={{ mb: 1.5 }}>
                <TextField
                    fullWidth
                    size="small"
                    placeholder="Search WO number, description, or notes…"
                    value={search}
                    onChange={handleSearchChange}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <SearchIcon fontSize="small" />
                            </InputAdornment>
                        ),
                    }}
                />
            </Box>

            <Box sx={{ mb: 1.5 }}>
                <Box
                    sx={{
                        display: "grid",
                        gridTemplateColumns: { xs: "repeat(3, 1fr)", sm: `repeat(${STATUS_FILTERS.length + 1}, 1fr)` },
                        gap: 0.75,
                        width: "100%",
                    }}
                >
                    {STATUS_FILTERS.map((s) => (
                        <ToggleButton
                            key={s.value}
                            value={s.value}
                            selected={statusFilter === s.value}
                            onChange={() => setStatusFilter(s.value)}
                            size="small"
                            sx={{
                                textTransform: "none",
                                border: "1px solid",
                                borderColor: "primary.main",
                                borderRadius: "4px !important",
                            }}
                        >
                            {s.label}
                        </ToggleButton>
                    ))}
                    <ToggleButton
                        value="archived"
                        selected={showArchived}
                        onChange={() => setShowArchived((v) => !v)}
                        size="small"
                        sx={{
                            textTransform: "none",
                            border: "1px solid",
                            borderColor: "primary.main",
                            borderRadius: "4px !important",
                        }}
                    >
                        Archived
                    </ToggleButton>
                </Box>
            </Box>

            <Box sx={{ display: "flex", flexDirection: { xs: "column", sm: "row" }, gap: 1.5, mb: 1.5 }}>
                <Box sx={{ flex: 1 }}>
                    <ProjectPicker
                        value={projectFilter}
                        onChange={setProjectFilter}
                        label="Filter by Project"
                        size="small"
                    />
                </Box>
                <TextField
                    label="Planned From"
                    type="date"
                    size="small"
                    value={dateFrom}
                    onChange={(e) => setDateFrom(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                    sx={{ flex: 1 }}
                />
                <TextField
                    label="Planned To"
                    type="date"
                    size="small"
                    value={dateTo}
                    onChange={(e) => setDateTo(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                    sx={{ flex: 1 }}
                />
            </Box>

            {activeFilterCount > 0 && (
                <Box sx={{ mb: 1.5 }}>
                    <Chip
                        label={`Clear ${activeFilterCount} filter${activeFilterCount > 1 ? "s" : ""}`}
                        size="small"
                        color="primary"
                        variant="outlined"
                        onDelete={clearFilters}
                        onClick={clearFilters}
                    />
                </Box>
            )}

            {showArchived && !hasArchiveFilter ? (
                <Box sx={{ textAlign: "center", py: 6, color: "text.secondary" }}>
                    <Typography variant="body2">
                        Search by WO number, project, or a planned date range to view archived
                        work orders.
                    </Typography>
                </Box>
            ) : (
                <WorkOrdersList
                    workOrders={workOrders}
                    loading={loading}
                    hasMore={hasMore}
                    onLoadMore={handleLoadMore}
                    onView={(wo) => setViewDialog({ open: true, item: wo })}
                    showArchived={showArchived}
                    onArchive={(wo) => setArchiveTarget(wo)}
                    onRestore={(wo) => setRestoreTarget(wo)}
                />
            )}

            <WorkOrderViewDialog
                open={viewDialog.open}
                onClose={() => setViewDialog({ open: false, item: null })}
                workOrder={viewDialog.item}
                onChanged={handleChanged}
            />

            <ArchiveDialog
                open={Boolean(archiveTarget)}
                workOrder={archiveTarget}
                onClose={() => setArchiveTarget(null)}
                onArchived={async () => runSearch(1, false)}
            />

            <RestoreDialog
                open={Boolean(restoreTarget)}
                workOrder={restoreTarget}
                onClose={() => setRestoreTarget(null)}
                onRestored={async () => runSearch(1, false)}
            />
        </Box>
    );
}
